import { useEffect, useCallback, useRef } from 'react';
import type { CrossTabMessage } from 'src/services/Session';

/**
 * useCrossTabSessionSync Hook
 *
 * Synchronizes session state across browser tabs using BroadcastChannel API.
 * When user is active in one tab, all tabs are kept alive. When user logs out
 * in one tab, all tabs are logged out.
 *
 * Features:
 * - Activity synchronization across tabs
 * - Warning modal synchronization
 * - Session extension synchronization
 * - Logout synchronization
 * - Automatic cleanup on unmount
 *
 * THREE THINGS TO KNOW BEFORE CHANGING THIS (#2492):
 *
 * 1. BroadcastChannel delivers to every channel with the same name EXCEPT the
 *    sender — including sibling channels in the SAME document. When each
 *    useAuth instance opened its own channel, one tap made the in-tab instances
 *    broadcast activity at each other in an unbounded loop. So there is exactly
 *    ONE channel object per tab per name, shared at module scope: a channel
 *    never receives its own posts, which makes same-tab feedback structurally
 *    impossible no matter how many instances mount.
 * 2. `enabled` controls only whether THIS instance LISTENS. Broadcasting always
 *    works, from any instance — user-initiated logout runs through useLogout,
 *    which is not the session owner, and it still has to reach other tabs.
 * 3. Handlers are held in a ref. Putting them in the effect's dependency array
 *    re-registered the listener on every render, which churned and dropped
 *    in-flight messages.
 */

/**
 * One channel per name for the lifetime of the document — see note 1 above.
 * Never closed: it is shared, so no single consumer may close it, and the
 * browser reclaims it with the document.
 */
const sharedChannels = new Map<string, BroadcastChannel | null>();

const getSharedChannel = (channelName: string): BroadcastChannel | null => {
  if (typeof BroadcastChannel === 'undefined') {
    return null;
  }

  if (!sharedChannels.has(channelName)) {
    try {
      sharedChannels.set(channelName, new BroadcastChannel(channelName));
    } catch (error) {
      console.error('[useCrossTabSessionSync] Failed to initialize BroadcastChannel:', error);
      // Cache the failure so we don't retry on every render.
      sharedChannels.set(channelName, null);
    }
  }

  return sharedChannels.get(channelName) ?? null;
};

interface UseCrossTabSessionSyncOptions {
  /** Channel name for BroadcastChannel (default: 'admin-session') */
  channelName?: string;
  /** Enable cross-tab sync (default: true) */
  enabled?: boolean;
  /** Callback when activity detected in another tab */
  onActivityInOtherTab?: (timestamp: number) => void;
  /** Callback when warning shown in another tab */
  onWarningInOtherTab?: () => void;
  /** Callback when session extended in another tab */
  onExtendedInOtherTab?: () => void;
  /** Callback when logout triggered in another tab */
  onLogoutInOtherTab?: () => void;
}

export const useCrossTabSessionSync = (
  options: UseCrossTabSessionSyncOptions = {},
): {
  broadcastActivity: (timestamp?: number) => void;
  broadcastWarning: () => void;
  broadcastExtended: () => void;
  broadcastLogout: () => void;
} => {
  const { channelName = 'admin-session', enabled = true } = options;

  // Latest handlers, read at message time. See note 3 — these must NOT be
  // effect dependencies.
  const handlersRef = useRef(options);
  handlersRef.current = options;

  // Subscribe to messages from other tabs. Only the enabled instance listens.
  useEffect(() => {
    if (!enabled) return;

    const channel = getSharedChannel(channelName);
    if (!channel) return;

    const onMessage = (event: MessageEvent<CrossTabMessage>) => {
      const message = event?.data;

      // Validate message exists and has required properties
      if (!message || !message.type) {
        console.warn('[useCrossTabSessionSync] Received invalid message:', message);
        return;
      }

      const handlers = handlersRef.current;

      switch (message.type) {
        case 'activity': {
          handlers.onActivityInOtherTab?.(message.timestamp || Date.now());
          break;
        }

        case 'warning': {
          handlers.onWarningInOtherTab?.();
          break;
        }

        case 'extended': {
          handlers.onExtendedInOtherTab?.();
          break;
        }

        case 'logout': {
          handlers.onLogoutInOtherTab?.();
          break;
        }
      }
    };

    // addEventListener rather than `onmessage =` so a second listener can't
    // silently clobber the first if two instances ever enable at once.
    channel.addEventListener('message', onMessage);

    return () => {
      channel.removeEventListener('message', onMessage);
    };
  }, [enabled, channelName]);

  // Unconditional — see note 2. Call sites decide when a broadcast is
  // appropriate; a channel never delivers to itself, so this cannot feed back
  // into this tab.
  const post = useCallback(
    (message: CrossTabMessage) => {
      const channel = getSharedChannel(channelName);
      if (!channel) return;

      try {
        channel.postMessage(message);
      } catch (error) {
        console.error(`[useCrossTabSessionSync] Failed to broadcast ${message.type}:`, error);
      }
    },
    [channelName],
  );

  // Broadcast activity to other tabs
  const broadcastActivity = useCallback(
    (timestamp: number = Date.now()) => post({ type: 'activity', timestamp }),
    [post],
  );

  // Broadcast warning to other tabs
  const broadcastWarning = useCallback(
    () => post({ type: 'warning', timestamp: Date.now() }),
    [post],
  );

  // Broadcast session extension to other tabs
  const broadcastExtended = useCallback(
    () => post({ type: 'extended', timestamp: Date.now() }),
    [post],
  );

  // Broadcast logout to other tabs
  const broadcastLogout = useCallback(
    () => post({ type: 'logout', timestamp: Date.now() }),
    [post],
  );

  return {
    broadcastActivity,
    broadcastWarning,
    broadcastExtended,
    broadcastLogout,
  };
};
